import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

interface MarketNewsConfig extends WidgetConfig {
  interval?: '1d' | '5d' | '1m';
  onIntervalChange?: (interval: '1d' | '5d' | '1m') => void;
  topic?: 'stock' | 'press';
  onTopicChange?: (topic: 'stock' | 'press') => void;
}

export class TraderverseMarketNews {
  private container: HTMLElement;
  private config: MarketNewsConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private interval: '1d' | '5d' | '1m';
  private topic: 'stock' | 'press';

  constructor(config: MarketNewsConfig) {
    this.interval = config.interval || '5d';
    this.topic = config.topic || 'stock';
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<MarketNewsConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      interval: '5d',
      topic: 'stock'
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const symbolOptions = this.fetchSymbols()
      .map(s => `<option value="${s.value}">${s.label}</option>`).join('');

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Latest News</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <i class="pi pi-ellipsis-v"></i>
          </div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            <div class="item dropStyle01">
           
            </div>
            <span></span>
            <div class="item">
              <select class="interval-dropdown">
                <option value="1d">1 Day</option>
                <option value="5d" selected>5 Days</option>
                <option value="1m">1 Month</option>
              </select>
            </div>
            <div class="item">
              <select class="topic-dropdown">
                <option value="stock" selected>Stock</option>
                <option value="press">Press</option>
              </select>
            </div>
          </div>
          <div class="chartDv" id="newsContainer">
          </div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    // if (this.config.isSymbolSelectable) {
    //   const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
    //   const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
    //   if (placeholder && dropdown) {
    //     placeholder.replaceWith(dropdown);
    //   }
    // }
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    const intervalDropdown = this.shadowRoot.querySelector('.interval-dropdown') as HTMLSelectElement;
    const topicDropdown = this.shadowRoot.querySelector('.topic-dropdown') as HTMLSelectElement;

    symbolDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.config.symbol = value;
      this.setupChart(value);
    });

    intervalDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value as '1d' | '5d' | '1m';
      this.interval = value;
      if (this.config.onIntervalChange) {
        this.config.onIntervalChange(value);
      }
      this.emit('intervalChange', { interval: value });
      this.setupChart(this.config.symbol!);
    });

    topicDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value as 'stock' | 'press';
      this.topic = value;
      if (this.config.onTopicChange) {
        this.config.onTopicChange(value);
      }
      this.emit('topicChange', { topic: value });
      this.setupChart(this.config.symbol!);
    });

    if (symbolDropdown && this.config.symbol) symbolDropdown.value = this.config.symbol;
    if (intervalDropdown) intervalDropdown.value = this.interval;
    if (topicDropdown) topicDropdown.value = this.topic;
  }




  private async setupChart(symbol: string): Promise<void> {
    try {
      const currentDate = new Date();
      const endDate = currentDate.toISOString().split('T')[0];
      const startDate = new Date(currentDate);
      switch (this.interval) {
        case '1d':
          startDate.setDate(currentDate.getDate() - 1);
          break;
        case '5d':
          startDate.setDate(currentDate.getDate() - 5);
          break;
        case '1m':
        default:
          startDate.setMonth(currentDate.getMonth() - 1);
          break;
      }

      const startDateStr = startDate.toISOString().split('T')[0];

      const topic = this.topic;

      const payload = {
        browser_detail: "Chrome",
        browser_version: "139.0.0.0",
        device: "Windows",
        start_date: startDateStr,
        end_date: endDate,
        entity: "markets",
        entity_type: "page",
        // symbol: symbol,
        topic: topic,
      };

      const response = await apiGet({
        url: `${APIURL}/v1/discover/news`,
        body: payload,
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const data = await response.json();
      this.currentData = data.data || [];

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }



  private updateDisplay(): void {
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }

    const newsContainer = this.shadowRoot.querySelector('#newsContainer') as HTMLElement;
    const chartDv = this.shadowRoot.querySelector('.chartDv');

    const hasData =
        Array.isArray(this.currentData) &&
        this.currentData.length > 0;

    if (chartDv) {
        if (hasData) {
            chartDv.setAttribute(
                'data-testing-id',
                'market-sector-correlation-chartdv'
            );
        } else {
            chartDv.removeAttribute('data-testing-id');
        }
    }

    if (!newsContainer) return;

    if (!hasData) {
      newsContainer.innerHTML = getNoDataHTML();
      return;
    }

    void this.renderMarketNews();
  }


  private async renderMarketNews(): Promise<void> {
    const container = this.shadowRoot.querySelector('#newsContainer');
    if (!container) return;

    const dummyLog = 'https://firebasestorage.googleapis.com/v0/b/signin-3c761.appspot.com/o/news-placeholder.png?alt=media&token=26b934ee-c082-4ee0-8f1c-f740f9e3b5fb';

    const checkImage = async (url: string): Promise<boolean> => {
      try {
        const res = await fetch(url, { method: 'HEAD' });
        const contentType = res.headers.get('Content-Type') || '';
        return res.ok && contentType.startsWith('image/');
      } catch {
        return false;
      }
    };

    const newsItems = await Promise.all(
      this.currentData.map(async (item: any) => {
        const validImage = item.banner_image && (await checkImage(item.banner_image));
        const imageToUse = validImage ? item.banner_image : dummyLog;

        return `
        <a href="${item.url}" target="_blank" class="newsItem" >
        <div class="item">
          <div class="textCol">
            <legend>
              <img src="${imageToUse}" onerror="this.onerror=null;this.src='${dummyLog}';" />
              <span>${item.source} • ${this.formatDate(item.last_updated_at)}</span>
            </legend>
            <h3>${item.title}</h3>
          </div>
          <div class="imgCol">
            <figure><img src="${imageToUse}" onerror="this.onerror=null;this.src='${dummyLog}';" /></figure>
          </div>
        </div>
        </a>
      `;
      })
    );

    container.innerHTML = `
    <div class="newsMain">
      <div class="newsList active">
        ${newsItems.join('')}
      </div>
    </div>
  `;
  }





  private formatDate(dateString: string): string {
    if (!dateString) return '';
  
    const date = new Date(dateString);
  
    if (isNaN(date.getTime())) return dateString;
  
    const formattedDate = date.toISOString().split('T')[0];
  
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  
    return `${formattedDate} ${formattedTime}`;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseMarketNews: typeof TraderverseMarketNews; } }
if (typeof window !== 'undefined') { window.TraderverseMarketNews = TraderverseMarketNews; }
